/**
 * The rendered message, in the frame everything previews it through.
 *
 * One component because of the sandbox. Email markup is a whole document with
 * its own inline styles; dropped into the page it would inherit and disturb the
 * application's own, and `srcDoc` rather than `dangerouslySetInnerHTML`, which
 * Biome forbids outright and which would be the wrong tool anyway.
 *
 * **Links work, and nothing else does.** `sandbox=""` blocked navigation along
 * with everything else, so a preview could not answer "does this button go where
 * I think it does" — which is half of what somebody checks a message for. Two
 * permissions are granted and no more: a link may open a window, and what it
 * opens is not itself sandboxed, because a destination site crippled by our
 * sandbox tells the reader nothing. Scripts stay off. The markup has already
 * been through `safeEmailHtml` by the time it gets here — it is rendered by the
 * same path that freezes a campaign — so there is nothing in it to run, and the
 * sandbox is the second lock rather than the only one.
 *
 * Every link in a sent message carries `target="_blank"`, added by the
 * sanitiser, which is what `allow-popups` is for: without it a click would be
 * swallowed silently.
 */

/** The three widths worth looking at. A phone is what most people read on. */
export type PreviewDevice = 'desktop' | 'tablet' | 'mobile'

export const PREVIEW_DEVICES: readonly (readonly [PreviewDevice, string])[] = [
  ['desktop', 'Desktop'],
  ['tablet', 'Tablet'],
  ['mobile', 'Mobile'],
]

/**
 * Literal classes, never assembled.
 *
 * Tailwind generates classes by scanning source text, so `w-[${n}px]` is a
 * class that does not exist in the stylesheet and silently renders at zero —
 * the same trap `Table`'s minimum widths and `BarChart`'s heights document.
 */
const WIDTHS: Record<PreviewDevice, string> = {
  desktop: 'w-full',
  tablet: 'w-[768px] max-w-full',
  mobile: 'w-[390px] max-w-full',
}

export function MessageFrame({
  html,
  device,
  className = '',
}: {
  readonly html: string
  readonly device: PreviewDevice
  /** How tall it is, which is the caller's business rather than this one's. */
  readonly className?: string
}) {
  return (
    <iframe
      title="Message preview"
      srcDoc={html}
      sandbox="allow-popups allow-popups-to-escape-sandbox"
      className={`border border-line bg-white ${WIDTHS[device]} ${className}`}
    />
  )
}
